import { Check, CheckCircle2, Copy, Info, TriangleAlert, X, XCircle } from "lucide-react";
import { ReactNode, useState } from "react";
import clsx from "clsx";

export type ToastVariant = "success" | "error" | "warning" | "info";

interface ToastProps {
  variant?: ToastVariant;
  children: ReactNode;
  onClose: () => void;
  /** Se valorizzato, mostra un pulsante che copia questo testo negli appunti. */
  copyValue?: string;
}

const VARIANT_STYLES: Record<ToastVariant, { container: string; icon: ReactNode }> = {
  success: {
    container: "bg-white border-l-4 border-l-[#22c55e]",
    icon: <CheckCircle2 className="h-5 w-5 text-[#22c55e]" aria-hidden="true" />,
  },
  error: {
    container: "bg-white border-l-4 border-l-accent",
    icon: <XCircle className="h-5 w-5 text-accent" aria-hidden="true" />,
  },
  warning: {
    container: "bg-white border-l-4 border-l-[#f97316]",
    icon: <TriangleAlert className="h-5 w-5 text-[#f97316]" aria-hidden="true" />,
  },
  info: {
    container: "bg-white border-l-4 border-l-primary",
    icon: <Info className="h-5 w-5 text-primary" aria-hidden="true" />,
  },
};

export function Toast({ variant = "info", children, onClose, copyValue }: ToastProps) {
  const styles = VARIANT_STYLES[variant];
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");

  async function handleCopy() {
    if (!copyValue) return;
    try {
      // navigator.clipboard esiste solo in contesto sicuro (https o localhost).
      // Dietro http la proprietà è undefined: si ricade sulla selezione manuale.
      if (!navigator.clipboard) throw new Error("clipboard non disponibile");
      await navigator.clipboard.writeText(copyValue);
      setCopyState("done");
      window.setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      // Meglio dirlo che lasciar credere che sia copiata: la password è ancora
      // a schermo e resta selezionabile a mano.
      setCopyState("failed");
      window.setTimeout(() => setCopyState("idle"), 3000);
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        "flex items-start gap-3 rounded-lg px-4 py-3 shadow-card transition-all",
        styles.container
      )}
    >
      <span className="mt-0.5 shrink-0">{styles.icon}</span>
      <div className="flex flex-1 flex-col gap-2">
        <p className="text-sm text-ink">{children}</p>
        {copyValue && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 self-start rounded-md border border-line px-2 py-1 text-xs font-semibold text-ink transition-colors hover:bg-black/5"
            >
              {copyState === "done" ? (
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {copyState === "done" ? "Copiata" : "Copia"}
            </button>
            {copyState === "failed" && (
              <span className="text-xs text-ink/50">Copia non riuscita: selezionala a mano.</span>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Chiudi notifica"
        className="shrink-0 rounded p-0.5 text-ink/40 transition-colors hover:bg-black/5 hover:text-ink"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
